import { useCallback, useMemo } from 'react';
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  medicalRecordsApi,
  type MedicalRecord,
  FilterMedicalRecordDto,
  CreateMedicalRecordDto,
  UpdateMedicalRecordDto,
} from '../services/api/medicalRecordsApi';
import { toast } from 'sonner';
import { extractErrorMessage } from '@/utils/errorHandling';

// Query keys factory
export const medicalRecordsKeys = {
  all: ['medical-records'] as const,
  lists: () => [...medicalRecordsKeys.all, 'list'] as const,
  list: (filters: FilterMedicalRecordDto) =>
    [...medicalRecordsKeys.lists(), filters] as const,
  details: () => [...medicalRecordsKeys.all, 'detail'] as const,
  detail: (id: string) => [...medicalRecordsKeys.details(), id] as const,
  outcomes: (id: string) =>
    [...medicalRecordsKeys.detail(id), 'outcomes'] as const,
};

// Get all medical records
export const useMedicalRecords = (filters: FilterMedicalRecordDto = {}) => {
  return useQuery({
    queryKey: medicalRecordsKeys.list(filters),
    queryFn: () => medicalRecordsApi.getMedicalRecords(filters),
    staleTime: 30000, // 30 seconds
  });
};

/**
 * How many visits the chart pulls per request.
 *
 * The chart used to ask for `limit: 100` in one shot and render all of
 * it. That is two failures at once: a patient with more than 100 visits
 * silently lost the rest — including from the "last visit" date and the
 * section counts, which were computed off the truncated array — and a
 * patient with exactly 100 paid the full DOM cost on open.
 */
export const CHART_VISITS_PAGE = 25;

export interface PagedRecords {
  records: MedicalRecord[];
  /** Total visits on the server, not the number loaded. */
  total: number;
  loaded: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
}

/**
 * A patient's visits, newest first, a page at a time.
 *
 * `total` comes from the envelope the backend already returns
 * (`createPaginatedResponse`), so the chart can say "25 of 187" instead
 * of implying the 25 it has are all there is.
 */
export const usePatientMedicalRecords = (
  patientId: string | undefined,
  /**
   * Held false while the chart bundle is in flight — it seeds this exact
   * key with page 1, and fetching before it lands would spend the request
   * the bundle exists to save.
   */
  enabled = true,
): PagedRecords => {
  const query = useInfiniteQuery({
    queryKey: [...medicalRecordsKeys.lists(), 'byPatient', patientId],
    enabled: !!patientId && enabled,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      medicalRecordsApi.getMedicalRecords({
        patientId,
        page: pageParam as number,
        limit: CHART_VISITS_PAGE,
      }),
    getNextPageParam: (lastPage: PaginatedRecordsEnvelope) => {
      const p = lastPage?.pagination;
      if (!p) return undefined;
      return p.hasNextPage ? p.page + 1 : undefined;
    },
    staleTime: 30000,
  });

  const pages = query.data?.pages as PaginatedRecordsEnvelope[] | undefined;

  // Memoised: `records` is the identity the whole chart's `useMemo`s key
  // off (the merged stream, the day grouping, the render window). A fresh
  // array every render would rebuild all of it whenever anything else on
  // the page re-renders.
  const records = useMemo(
    () => (pages ?? []).flatMap((p) => p?.data ?? []),
    [pages],
  );

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  // Stable across renders, so the stream's scroll sentinel doesn't
  // re-subscribe — and re-fire — every time the page re-renders.
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // `total` is absent on the very first render and on any legacy response
  // that predates the pagination envelope — fall back to what we hold so
  // the footer never claims a patient has fewer visits than are on screen.
  const total = pages?.[0]?.pagination?.total ?? records.length;
  const isLoading = query.isLoading;

  return useMemo(
    () => ({
      records,
      total,
      loaded: records.length,
      hasMore: !!hasNextPage,
      isLoading,
      isLoadingMore: isFetchingNextPage,
      loadMore,
    }),
    [records, total, hasNextPage, isLoading, isFetchingNextPage, loadMore],
  );
};

/** The `{ success, data, pagination }` envelope `/medical-records` returns. */
interface PaginatedRecordsEnvelope {
  data?: MedicalRecord[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

// Get single medical record
export const useMedicalRecord = (id: string) => {
  return useQuery({
    queryKey: medicalRecordsKeys.detail(id),
    queryFn: () => medicalRecordsApi.getMedicalRecordById(id),
    enabled: !!id,
  });
};

// Everything this encounter led to (Rx / labs / imaging / referrals /
// follow-ups), scoped to the note via medicalRecordId. Drives the
// "What this visit produced" rollup panel.
export const useEncounterOutcomes = (id: string) => {
  return useQuery({
    queryKey: medicalRecordsKeys.outcomes(id),
    queryFn: () => medicalRecordsApi.getEncounterOutcomes(id),
    enabled: !!id,
    staleTime: 15000,
  });
};

// Create medical record
export const useCreateMedicalRecord = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateMedicalRecordDto) =>
      medicalRecordsApi.createMedicalRecord(data),
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: medicalRecordsKeys.lists() });
      toast.success('Medical record created successfully');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to create medical record');
    },
  });
};

// Update medical record
export const useUpdateMedicalRecord = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateMedicalRecordDto }) =>
      medicalRecordsApi.updateMedicalRecord(id, data),
    onSuccess: (_, variables) => {
      // Invalidate specific record and lists
      queryClient.invalidateQueries({
        queryKey: medicalRecordsKeys.detail(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: medicalRecordsKeys.lists() });
      toast.success('Medical record updated successfully');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to update medical record');
    },
  });
};

// Sign medical record — PIN is the clinician's 4-digit attestation
// factor. Callers should drive this through the AttestationModal which
// handles first-time setup + lockout messaging; the bare hook stays for
// future programmatic flows.
export const useSignMedicalRecord = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, pin }: { id: string; pin: string }) =>
      medicalRecordsApi.signMedicalRecord(id, pin),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: medicalRecordsKeys.detail(id),
      });
      queryClient.invalidateQueries({ queryKey: medicalRecordsKeys.lists() });
    },
  });
};

// Publish a signed encounter to the patient portal.
export const usePublishMedicalRecord = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => medicalRecordsApi.publishToPatient(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: medicalRecordsKeys.detail(id),
      });
      queryClient.invalidateQueries({ queryKey: medicalRecordsKeys.lists() });
    },
  });
};

export const useUnpublishMedicalRecord = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => medicalRecordsApi.unpublishFromPatient(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: medicalRecordsKeys.detail(id),
      });
      queryClient.invalidateQueries({ queryKey: medicalRecordsKeys.lists() });
    },
  });
};

// Delete medical record
export const useDeleteMedicalRecord = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => medicalRecordsApi.deleteMedicalRecord(id),
    onSuccess: () => {
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: medicalRecordsKeys.lists() });
    },
  });
};
